import type { LevelData } from "../types/level";
import type { LevelState } from "../model/LevelState";
import { getPlankDrawInstructions, getTerrainDrawInstructions, getWorldBounds } from "./draw-model";
import { RENDER_PALETTE, TILE_SIZE } from "./render-palette";

export interface LevelRenderModel {
  readonly state: LevelState;
  readonly data: LevelData;
}

const LADDER_RUNG_SPACING = 6;
const LADDER_RUNG_MARGIN = 6;
const LADDER_RUNG_HEIGHT = 2;
const LADDER_RAIL_WIDTH = 2;
const LADDER_RAIL_MARGIN = 4;
const LADDER_ACCENT_COLOR = 0x6b4a10;

export class LevelRenderer {
  private readonly graphics: Phaser.GameObjects.Graphics;

  public constructor(scene: Phaser.Scene) {
    this.graphics = scene.add.graphics();
  }

  public render(model: LevelRenderModel): void {
    this.graphics.clear();

    const instructions = getTerrainDrawInstructions(model.data.grid);
    const { width: worldWidth, height: worldHeight } = getWorldBounds(model.data.grid);

    this.graphics.fillStyle(RENDER_PALETTE.background);
    this.graphics.fillRect(0, 0, worldWidth, worldHeight);

    for (const inst of instructions) {
      this.graphics.fillStyle(inst.color);
      this.graphics.fillRect(inst.x, inst.y, inst.width, inst.height);
    }

    const planks = getPlankDrawInstructions(model.data.grid);
    for (const plank of planks) {
      this.graphics.fillStyle(plank.color);
      this.graphics.fillRect(plank.x, plank.y, plank.width, plank.height);
    }

    for (const inst of instructions) {
      if (inst.type === "ladder") {
        for (let ry = inst.y + LADDER_RUNG_SPACING; ry < inst.y + TILE_SIZE - 1; ry += LADDER_RUNG_SPACING) {
          this.graphics.fillStyle(LADDER_ACCENT_COLOR);
          this.graphics.fillRect(
            inst.x + LADDER_RUNG_MARGIN,
            ry,
            inst.width - LADDER_RUNG_MARGIN * 2,
            LADDER_RUNG_HEIGHT,
          );
        }

        this.graphics.fillStyle(LADDER_ACCENT_COLOR);
        this.graphics.fillRect(inst.x + LADDER_RAIL_MARGIN, inst.y, LADDER_RAIL_WIDTH, inst.height);
        this.graphics.fillRect(
          inst.x + inst.width - LADDER_RAIL_MARGIN - LADDER_RAIL_WIDTH,
          inst.y,
          LADDER_RAIL_WIDTH,
          inst.height,
        );
      }
    }

    this.graphics.setDepth(0);
  }

  public setMask(mask: Phaser.Display.Masks.BitmapMask): void {
    this.graphics.setMask(mask);
  }

  public destroy(): void {
    this.graphics.destroy();
  }
}
